// Auth store + context. JWT in memory + refresh-token in localStorage.
// Multi-tab sync via BroadcastChannel (PRD-01 US-AUTH-05).

const ACCESS_KEY = "bb:access";
const REFRESH_KEY = "bb:refresh";
const TENANT_KEY = "bb:tenantSlug";
const USER_KEY = "bb:user";

export interface CurrentUser {
  id: string;
  email: string;
  role: "client" | "counselor" | "municipality" | "admin" | "super_admin";
  tenantId: string;
}

type AuthListener = () => void;

const channel = "BroadcastChannel" in globalThis ? new BroadcastChannel("bb-auth") : null;
const listeners = new Set<AuthListener>();

function notify() {
  for (const fn of listeners) fn();
}

channel?.addEventListener("message", (event) => {
  if (event.data === "logout") notify();
});

export const authStore = {
  getAccessToken(): string | null {
    return sessionStorage.getItem(ACCESS_KEY);
  },
  getRefreshToken(): string | null {
    return localStorage.getItem(REFRESH_KEY);
  },
  getTenantSlug(): string | null {
    return localStorage.getItem(TENANT_KEY);
  },
  getUser(): CurrentUser | null {
    const raw = localStorage.getItem(USER_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as CurrentUser;
    } catch {
      return null;
    }
  },
  setTokens(access: string, refresh: string): void {
    sessionStorage.setItem(ACCESS_KEY, access);
    localStorage.setItem(REFRESH_KEY, refresh);
    notify();
  },
  setSession(access: string, refresh: string, user: CurrentUser, tenantSlug: string): void {
    sessionStorage.setItem(ACCESS_KEY, access);
    localStorage.setItem(REFRESH_KEY, refresh);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    localStorage.setItem(TENANT_KEY, tenantSlug);
    notify();
  },
  clear(): void {
    sessionStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(USER_KEY);
    notify();
    channel?.postMessage("logout");
  },
  isAuthenticated(): boolean {
    return Boolean(this.getRefreshToken() && this.getUser());
  },
  subscribe(fn: AuthListener): () => void {
    listeners.add(fn);
    return () => listeners.delete(fn);
  },
};
